import React, { useState, useEffect, useMemo } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  subscribeToPlan,
  getSubscriptions,
  getPlans,
  getCards,
  getInvoices,
  cancelSubscription,
  setAutoRenew,
  downloadSubscriptionReceipt,
} from "../../../services/stripe_services";
import PaymentMethod from "./payments/PaymentMethod";
import PaymentStatus from "./payments/PaymentStatus";
import SubscriptionOverview from "./payments/SubscriptionOverview";
import { ConfirmModal } from "../../../components/ConfirmModal";
import EmptyInvoices from "./payments/EmptyInvoices";
import SubscriptionStatusPill from "./payments/SubscriptionStatusPill";
import { fetchAndApplySubscriptionAccess } from "../../../utils/subscriptionAccess";
import { getApiErrorMessage } from "../../../utils/getApiErrorMessage";
import {
  getCardId,
  normalizeCardList,
} from "../../../utils/billingSelection";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const PaymentsPage = () => {
  const user = useSelector((state) => state?.user);
  const isBillingAdmin = user?.role_id === 1 || user?.role_id === 2;
  const [activeTab, setActiveTab] = useState("subscriptions");

  // ── Subscriptions list state ──────────────────────────────────────────────
  const [subscriptions, setSubscriptions] = useState([]);
  const [fetchingSubscriptions, setFetchingSubscriptions] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);

  const handleDownloadReceipt = async (subscriptionId) => {
    try {
      setDownloadingId(subscriptionId);
      const { blob, filename } =
        await downloadSubscriptionReceipt(subscriptionId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(
        err?.message || "Failed to download receipt. Please try again.",
      );
    } finally {
      setDownloadingId(null);
    }
  };

  useEffect(() => {
    if (!isBillingAdmin) {
      return;
    }

    // Fetch on both tabs: the "Invoices" list needs it for display, and the
    // "Subscription" wizard needs it to mark plans the user is already on as
    // "Currently Active" so the same plan can't be re-purchased.
    if (activeTab === "subscriptions" || activeTab === "new") {
      fetchSubscriptionsList();
    }
  }, [activeTab, isBillingAdmin]);

  const fetchSubscriptionsList = async () => {
    try {
      setFetchingSubscriptions(true);
      const data = await getSubscriptions();
      if (data?.success) {
        setSubscriptions(data.subscriptions ?? data.data ?? []);
      }
    } catch {
      // silent
    } finally {
      setFetchingSubscriptions(false);
    }
  };

  // ── Plans (drive the status pill, the plan card and the empty state) ─────
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (!isBillingAdmin) {
      return;
    }

    (async () => {
      try {
        const data = await getPlans();
        if (data?.success) {
          setPlans(data.products ?? data.plans ?? data.data ?? []);
        }
      } catch {
        // Plans failing shouldn't break the page; the panel shows its loader.
      }
    })();
  }, [isBillingAdmin]);

  /** Single-plan product: the plan shown on the Subscription tab. */
  const currentPlan = useMemo(() => plans[0] ?? null, [plans]);

  const activeSubscription = useMemo(
    () =>
      subscriptions.find((s) =>
        ["active", "trialing"].includes((s.status ?? "").toLowerCase()),
      ) ?? null,
    [subscriptions],
  );

  const activePlanName = useMemo(
    () =>
      plans.find((plan) => plan.id === activeSubscription?.stripe_product_id)
        ?.name,
    [plans, activeSubscription],
  );

  // ── Subscription tab state ───────────────────────────────────────────────
  // subView: "overview" = plan panel, "card" = add/choose a payment method.
  const [subView, setSubView] = useState("overview");
  const [cardIntent, setCardIntent] = useState("add");
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [subscribeError, setSubscribeError] = useState(null);

  const handleCardSelect = (id, card) => {
    setSelectedCardId(id ?? null);
    setSelectedCard(card ?? null);
  };

  const goToSubscriptionTab = () => {
    setSubView("overview");
    setPaymentStatus(null);
    setActiveTab("new");
  };

  const fetchCardsList = async () => {
    try {
      setLoadingCards(true);
      const data = await getCards();
      setCards(normalizeCardList(data));
    } catch {
      setCards([]);
    } finally {
      setLoadingCards(false);
    }
  };

  useEffect(() => {
    if (isBillingAdmin) {
      fetchCardsList();
    }
  }, [isBillingAdmin]);

  /** Subscribe straight away when a card exists, otherwise collect one first. */
  const handleSubscribeClick = () => {
    const card = cards.find((item) => item?.is_default) ?? cards[0] ?? null;
    const cardId = getCardId(card);

    if (!cardId) {
      toast.error("Add a payment method first.");
      return;
    }

    // Charging money always asks first - same as cancelling.
    setConfirmSubscribe(true);
  };

  const confirmAndSubscribe = async () => {
    const card = cards.find((item) => item?.is_default) ?? cards[0] ?? null;
    const cardId = getCardId(card);

    setConfirmSubscribe(false);
    await handleSubscribe(currentPlan, cardId);
  };

  /** What the user is about to be charged, spelled out before we take money. */
  const subscriptionConfirmSummary = () => {
    const card = cards.find((item) => item?.is_default) ?? cards[0] ?? null;
    const price = currentPlan?.prices?.[0];
    const amount = price?.amount != null ? `$${price.amount}` : "";
    const interval = price?.recurring?.interval ?? "month";

    return (
      <div>
        <p className="mb-2">
          You're subscribing to <strong>{currentPlan?.name ?? "this plan"}</strong>.
        </p>
        <p className="mb-2">
          Your card{card?.last4 ? ` ending ${card.last4}` : ""} will be charged{" "}
          <strong>
            {amount} {String(price?.currency ?? "USD").toUpperCase()}
          </strong>{" "}
          today, and every {interval} after that until you cancel.
        </p>
        <p className="mb-0 text-muted small">
          You can cancel any time from this page.
        </p>
      </div>
    );
  };

  const handleManagePaymentMethod = () => {
    setCardIntent("add");
    setSubView("card");
  };

  const [invoices, setInvoices] = useState([]);

  const fetchInvoicesList = async () => {
    try {
      const data = await getInvoices(10);
      setInvoices(data?.success ? (data.invoices ?? []) : []);
    } catch {
      setInvoices([]);
    }
  };

  useEffect(() => {
    if (isBillingAdmin) {
      fetchInvoicesList();
    }
  }, [isBillingAdmin]);

  // ── Auto-renewal ─────────────────────────────────────────────────────────
  const [autoRenewPending, setAutoRenewPending] = useState(false);

  /**
   * Never flips the switch optimistically: the state shown always comes from
   * Stripe. On failure we surface the error and refetch, so the UI keeps
   * showing what Stripe actually holds.
   */
  const handleToggleAutoRenew = async (nextValue) => {
    if (!activeSubscription?.id) {
      toast.error("No active subscription to update.");
      return;
    }

    try {
      setAutoRenewPending(true);
      const data = await setAutoRenew(activeSubscription.id, nextValue);

      if (data?.success) {
        // The response carries the value read back from Stripe after the write,
        // so applying it is not optimistic - it is the verified state. Moving
        // the switch here avoids waiting on a second round-trip.
        setSubscriptions((prev) =>
          prev.map((item) =>
            item.id === activeSubscription.id
              ? {
                  ...item,
                  auto_renew: data.auto_renew,
                  status: data.status ?? item.status,
                  current_period_end:
                    data.current_period_end ?? item.current_period_end,
                  stripe_live: true,
                  stripe_error: null,
                }
              : item,
          ),
        );

        toast.success(
          data.message ??
            (data.auto_renew ? "Auto-renewal is on." : "Auto-renewal is off."),
        );
      } else {
        toast.error(
          getApiErrorMessage(data, "Couldn't update auto-renewal in Stripe."),
        );
        // Nothing was applied locally; refetch so the switch shows Stripe truth.
        await fetchSubscriptionsList();
      }
    } catch {
      toast.error("Couldn't reach Stripe. Auto-renewal is unchanged.");
      await fetchSubscriptionsList();
    } finally {
      setAutoRenewPending(false);
    }
  };

  // ── Cancel subscription ──────────────────────────────────────────────────
  const [confirmSubscribe, setConfirmSubscribe] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const handleCancelSubscription = async () => {
    if (!activeSubscription?.id) {
      toast.error("No active subscription to cancel.");
      setConfirmCancel(false);
      return;
    }

    try {
      setCancelling(true);
      const data = await cancelSubscription(activeSubscription.id);

      if (data?.success) {
        toast.success("Subscription cancelled.");
        setConfirmCancel(false);
        await fetchSubscriptionsList();
        await fetchAndApplySubscriptionAccess(user?.client_id);
      } else {
        toast.error(getApiErrorMessage(data, "Failed to cancel subscription."));
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCancelling(false);
    }
  };

  /** Card step finished: refresh cards, then subscribe if that was the intent. */
  const handleCardStepDone = async (cardId) => {
    await fetchCardsList();
    setSubView("overview");

    if (cardIntent === "subscribe" && cardId) {
      handleSubscribe(currentPlan, cardId);
    }
  };

  const handleSubscribe = async (planOverride, cardIdOverride) => {
    const plan = planOverride ?? currentPlan;
    const planId = planOverride?.id ?? currentPlan?.id;
    const cardId = cardIdOverride ?? selectedCardId;

    if (!planId || !plan) {
      toast.error("No plan available to subscribe to.");
      return;
    }

    if (!cardId) {
      toast.error("Add a payment method before subscribing.");
      setSubView("card");
      return;
    }

    const email = user?.email?.trim();
    const priceId = plan?.prices?.[0]?.id;
    const customerName = `${user?.first_name ?? ""} ${user?.last_name ?? ""}`.trim();

    if (!email) {
      toast.error("Your account email is missing. Please sign in again.");
      return;
    }

    if (!priceId) {
      toast.error("This plan has no price configured in Stripe.");
      return;
    }

    try {
      setSubscribing(true);

      const payload = {
        email,
        name: customerName || email,
        plan_id: planId,
        price_id: priceId,
        payment_method_id: String(cardId),
      };
      const data = await subscribeToPlan(payload);

      if (data?.success) {
        setPaymentStatus("success");
        toast.success("Subscription activated!");
        await fetchAndApplySubscriptionAccess(user?.client_id);
        fetchSubscriptionsList();
      } else {
        const errMsg = getApiErrorMessage(data, "Subscription failed.");
        setSubscribeError(errMsg);
        setPaymentStatus("error");
        toast.error(errMsg);
      }
    } catch {
      setSubscribeError("Something went wrong. Please try again.");
      setPaymentStatus("error");
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const handleRetry = () => {
    setPaymentStatus(null);
    setSubscribeError(null);
    setSubView("overview");
  };

  const handleDone = () => {
    setPaymentStatus(null);
    setSubView("overview");
    setActiveTab("subscriptions");
    fetchSubscriptionsList();
  };

  // ── Tab styles ────────────────────────────────────────────────────────────
  const tabStyle = (tab) => ({
    border: "none",
    background: "transparent",
    padding: "10px 24px",
    fontSize: "0.875rem",
    fontWeight: activeTab === tab ? 600 : 400,
    color: activeTab === tab ? "#7F56D9" : "#667085",
    borderBottom:
      activeTab === tab ? "2px solid #7F56D9" : "2px solid transparent",
    cursor: "pointer",
    transition: "all 0.2s",
    whiteSpace: "nowrap",
  });

  // ── Status badge helper ───────────────────────────────────────────────────
  const statusBadge = (status) => {
    const map = {
      active: { bg: "#ECFDF3", color: "#027A48" },
      trialing: { bg: "#EFF8FF", color: "#175CD3" },
      past_due: { bg: "#FFFAEB", color: "#B54708" },
      canceled: { bg: "#FEF3F2", color: "#B42318" },
      incomplete: { bg: "#F2F4F7", color: "#344054" },
    };
    const style = map[status?.toLowerCase()] ?? {
      bg: "#F2F4F7",
      color: "#344054",
    };
    return (
      <span
        className="badge rounded-pill px-3 py-2"
        style={{
          background: style.bg,
          color: style.color,
          fontSize: "0.75rem",
        }}
      >
        {status ?? "Unknown"}
      </span>
    );
  };

  if (!isBillingAdmin) {
    return (
      <div className="p-4">
        <div
          className="d-flex flex-column align-items-center justify-content-center rounded-3 px-4 py-5"
          style={{
            background: "#FFFFFF",
            boxShadow:
              "0 4px 6px -2px rgba(16,24,40,0.03), 0 12px 16px -4px rgba(16,24,40,0.08)",
            minHeight: 280,
          }}
        >
          <p className="fw-semibold mb-2" style={{ color: "#101828" }}>
            Billing is managed by your organization admin
          </p>
          <p className="text-muted small text-center mb-0" style={{ maxWidth: 420 }}>
            Only administrators can view invoices, subscribe, or manage payment
            methods. Contact your admin if you need access.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Banner */}
      <div
        className="d-flex align-items-center justify-content-between rounded-3 px-4 py-4 mb-4"
        style={{
          background: "linear-gradient(135deg, #7F56D9 0%, #9E77ED 100%)",
        }}
      >
        <div>
          <div className="text-white fw-bold fs-5 mb-1">Payments</div>
          <div className="text-white opacity-75 small">
            Manage your subscription, payment method and billing
          </div>
        </div>
        <SubscriptionStatusPill
          subscription={activeSubscription}
          planName={activePlanName}
        />
      </div>

      {/* Tabs */}
      <div
        className="rounded-3 overflow-hidden"
        style={{
          background: "#FFFFFF",
          boxShadow:
            "0 4px 6px -2px rgba(16,24,40,0.03), 0 12px 16px -4px rgba(16,24,40,0.08)",
        }}
      >
        {/* Tab header */}
        <div
          className="d-flex"
          style={{ borderBottom: "1px solid #F2F4F7", overflowX: "auto" }}
        >
          <button
            style={tabStyle("subscriptions")}
            onClick={() => setActiveTab("subscriptions")}
          >
            Invoices
          </button>
          <button style={tabStyle("new")} onClick={() => setActiveTab("new")}>
            Subscription
          </button>
        </div>

        {/* ── Tab: Subscriptions List ── */}
        {activeTab === "subscriptions" && (
          <div className="p-4 p-md-5" style={{ minHeight: 300 }}>
            <h5 className="fw-bold mb-1" style={{ color: "#101828" }}>
              Invoice history
            </h5>
            <p className="text-muted mb-4" style={{ fontSize: "0.95rem" }}>
              Receipts appear here after each payment.
            </p>

            {fetchingSubscriptions ? (
              <div className="text-center py-5">
                <div
                  className="spinner-border spinner-border-sm"
                  style={{ color: "#7F56D9" }}
                  role="status"
                />
                <p className="mt-3 small text-muted">
                  Loading subscriptions...
                </p>
              </div>
            ) : subscriptions.length === 0 ? (
              <EmptyInvoices onViewPlan={goToSubscriptionTab} />
            ) : (
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead style={{ background: "#F9FAFB" }}>
                    <tr>
                      <th className="small fw-semibold text-muted py-3 px-4 border-0">
                        Amount
                      </th>
                      <th className="small fw-semibold text-muted py-3 px-4 border-0">
                        Currency
                      </th>
                      <th className="small fw-semibold text-muted py-3 px-4 border-0">
                        Created At
                      </th>
                      <th className="small fw-semibold text-muted py-3 px-4 border-0">
                        Status
                      </th>
                      <th className="small fw-semibold text-muted py-3 px-4 border-0">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((s, i) => (
                      <tr key={i}>
                        <td className="py-3 px-4 small">
                          {s.amount != null
                            ? `$${Number(s.amount).toFixed(2)}`
                            : "—"}
                        </td>
                        <td className="py-3 px-4 small">
                          {s.currency ? s.currency.toUpperCase() : "—"}
                        </td>
                        <td className="py-3 px-4 small">
                          {s.created_at ?? s.start_date ?? "—"}
                        </td>
                        <td className="py-3 px-4 small">
                          {statusBadge(s.status)}
                        </td>
                        <td className="py-3 px-4 small">
                          <button
                            className="btn btn-sm d-inline-flex align-items-center gap-1 rounded-pill fw-medium"
                            style={{
                              background:
                                downloadingId === s.id ? "#E9D7FE" : "#F9F5FF",
                              color: "#7F56D9",
                              border: "1px solid #E9D7FE",
                              fontSize: "0.75rem",
                              minWidth: 130,
                              justifyContent: "center",
                            }}
                            disabled={downloadingId === s.id}
                            onClick={() => handleDownloadReceipt(s.id)}
                          >
                            {downloadingId === s.id ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm"
                                  role="status"
                                  style={{
                                    width: 12,
                                    height: 12,
                                    borderWidth: 2,
                                  }}
                                />
                                Downloading…
                              </>
                            ) : (
                              <>
                                <svg
                                  width="13"
                                  height="13"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                >
                                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                  <polyline points="7 10 12 15 17 10" />
                                  <line x1="12" y1="15" x2="12" y2="3" />
                                </svg>
                                Download Receipt
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── Tab: New Subscription (3-step flow) ── */}
        {activeTab === "new" && (
          <div className="p-4 p-md-5" style={{ minHeight: 400 }}>
            {paymentStatus === "success" && (
              <PaymentStatus
                status="success"
                planName={currentPlan?.name}
                onDone={handleDone}
              />
            )}

            {paymentStatus === "error" && (
              <PaymentStatus
                status="error"
                error={subscribeError}
                onRetry={handleRetry}
              />
            )}

            {!paymentStatus && (
              <Elements stripe={stripePromise}>
                {subView === "overview" ? (
                  <SubscriptionOverview
                    plan={currentPlan}
                    subscription={activeSubscription}
                    cards={cards}
                    loading={loadingCards && !currentPlan}
                    subscribing={subscribing}
                    onSubscribe={handleSubscribeClick}
                    onCancel={() => setConfirmCancel(true)}
                    cancelling={cancelling}
                    onToggleAutoRenew={handleToggleAutoRenew}
                    autoRenewPending={autoRenewPending}
                    onAddPaymentMethod={handleManagePaymentMethod}
                    onViewInvoices={() => setActiveTab("subscriptions")}
                    invoices={invoices}
                    invoiceCount={invoices.length}
                  />
                ) : (
                  <PaymentMethod
                    selectedCardId={selectedCardId}
                    onSelect={handleCardSelect}
                    onContinue={() => handleCardStepDone(selectedCardId)}
                    onBack={() => setSubView("overview")}
                    title={
                      cardIntent === "subscribe"
                        ? "Add a payment method"
                        : "Payment methods"
                    }
                    subtitle={
                      cardIntent === "subscribe"
                        ? "Add a card to start your subscription."
                        : "Add, remove or change the card charged for renewals."
                    }
                    continueLabel={
                      cardIntent === "subscribe" ? "Subscribe with this card" : "Done"
                    }
                  />
                )}
              </Elements>
            )}
          </div>
        )}
      </div>

      <ConfirmModal
        show={confirmSubscribe}
        loadingOnsubmit={subscribing}
        onClose={() => setConfirmSubscribe(false)}
        onSubmit={confirmAndSubscribe}
        title="Confirm your subscription"
        submitBtnTitle="Confirm and pay"
        closeBtnTitle="Not now"
        body={subscriptionConfirmSummary()}
      />

      <ConfirmModal
        show={confirmCancel}
        loadingOnsubmit={cancelling}
        onClose={() => setConfirmCancel(false)}
        onSubmit={handleCancelSubscription}
        title="Cancel subscription"
        submitBtnTitle="Cancel subscription"
        closeBtnTitle="Keep subscription"
        body="Your subscription will not renew. You keep access until the end of the current billing period."
      />
    </div>
  );
};

export default PaymentsPage;
